import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link, useNavigate, useParams, useSearchParams,
  type NavigateFunction,
} from 'react-router';
import {
  Plus, Check,
  Pencil, UserX, UserCheck, Trash2, BookOpen,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { cn } from '@repo/ui/src/lib/utils';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { Tags } from '../../components/Tags';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { ENROLLMENT_MODES, type EnrollmentMode, type PricingType, type StudentWithClient } from '@repo/shared-types';
import type { CreateEnrollmentInput } from '@repo/validation';
import { useStudent, useUpdateStudent, useDeleteStudent } from '../../hooks/useStudents';
import { ageOf } from '../../lib/studentName';
import { useEnrollments, useCreateEnrollment } from '../../hooks/useEnrollments';
import { useSubjects } from '../../hooks/useSubjects';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useClientOptions } from '../../hooks/useClients';
import { useCan } from '../../hooks/useCan';
import { ClientCombobox } from '../../components/ClientCombobox';
import { AssignDialog } from '../classes/AssignDialog';
import { PageHeader, type PageHeaderAction } from '../../components/PageHeader';
import { DetailSkeleton } from '../../components/DetailSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { StudentIdentityFields, type StudentIdentityValues } from './components/StudentIdentityFields';
import { EnrollmentCard } from './components/EnrollmentCard';
import { StudentActivitySection } from './components/StudentActivitySection';
import { ProgressTab } from './components/ProgressTab';
import { ProgressLinkBlock } from './components/ProgressLinkBlock';

export function StudentDetailPage() {
  const { t } = useTranslation('students');
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: student, isLoading, isError } = useStudent(id ?? null);

  if (isLoading) return <DetailSkeleton />;
  if (isError || !student) {
    // Same shape ClientDetail has always used for a missing record: the title
    // says what happened and the back link is the way out (13 §States).
    return (
      <div className="mx-auto w-full max-w-4xl p-8">
        <PageHeader size="md" back={{ label: t('detail.back'), onClick: () => navigate(-1) }} title={t('detail.notFound')} />
      </div>
    );
  }
  return <StudentDetailView student={student} />;
}

/**
 * The student header's actions and the dialogs behind them.
 *
 * This used to be a `StudentActions` component handed to `PageHeader` as
 * `extra`, for one reason: the shared kebab could neither disable its own
 * trigger nor wrap it in the RBAC tooltip. `PageHeaderAction.disabledReason`
 * (P2.7) does both, so the actions go back through the shared header and only
 * the dialogs stay here.
 *
 * A hook, not a component, because the dialogs must outlive the header's own
 * render tree: an AlertDialog unmounted mid-confirm would drop the mutation.
 */
function useStudentActions(student: StudentWithClient, navigate: NavigateFunction): {
  actions: PageHeaderAction[];
  dialogs: React.ReactNode;
} {
  const { t } = useTranslation('students');
  const { t: tCommon } = useTranslation('common');
  const { mutate: update, isPending: isUpdating } = useUpdateStudent(student.id);
  const { mutate: del, isPending: isDeleting } = useDeleteStudent();
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const canManageStudents = useCan('manage:students');
  const adminOnly = canManageStudents ? undefined : tCommon('rbac.adminOnly');

  const isInactive = student.status === 'INACTIVE';

  const handleDeactivate = () =>
    update(
      { status: 'INACTIVE' },
      {
        onSuccess: () => {
          setDeactivateConfirmOpen(false);
          toast.success(t('detail.actions.deactivateSuccess'));
        },
      },
    );

  const handleReactivate = () =>
    update({ status: 'ACTIVE' }, { onSuccess: () => toast.success(t('detail.actions.reactivateSuccess')) });

  const handleDelete = () =>
    del(student.id, {
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        toast.success(t('detail.actions.deleteSuccess'));
        navigate('/students');
      },
    });

  const actions: PageHeaderAction[] = [
    {
      // `common:edit` ("Sửa"), not `detail.actions.edit` ("Sửa học viên"): the
      // design's inline label is one word, and at 390 the action row already
      // carries "Thêm đăng ký" beside it (13 §3/§6). The title above says whose
      // record this is, so the button does not have to repeat it.
      key: 'edit',
      label: tCommon('edit'),
      icon: Pencil,
      variant: 'outline',
      onClick: () => setEditOpen(true),
      disabledReason: adminOnly,
    },
    isInactive
      ? {
          key: 'reactivate',
          label: t('detail.actions.reactivate'),
          icon: UserCheck,
          onClick: handleReactivate,
          disabled: isUpdating,
          disabledReason: adminOnly,
        }
      : {
          key: 'deactivate',
          label: t('detail.actions.deactivate'),
          icon: UserX,
          onClick: () => setDeactivateConfirmOpen(true),
          disabled: isUpdating,
          disabledReason: adminOnly,
        },
    {
      key: 'delete',
      label: t('detail.actions.delete'),
      icon: Trash2,
      onClick: () => setDeleteConfirmOpen(true),
      disabled: isDeleting,
      destructive: true,
      disabledReason: adminOnly,
    },
  ];

  const dialogs = (
    <>
      <EditStudentDialog student={student} open={editOpen} onOpenChange={setEditOpen} />

      <AlertDialog open={deactivateConfirmOpen} onOpenChange={setDeactivateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.actions.deactivateConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.actions.deactivateConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>{t('detail.actions.deactivateBullet1')}</li>
            <li>{t('detail.actions.deactivateBullet2')}</li>
            <li>{t('detail.actions.deactivateBullet3')}</li>
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('detail.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700">
              {t('detail.actions.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.actions.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.actions.deleteConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('detail.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {t('detail.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return { actions, dialogs };
}

function StudentDetailView({ student }: { student: StudentWithClient }) {
  const { t } = useTranslation('students');
  const { t: tCommon } = useTranslation('common');
  const { t: tProgress } = useTranslation('progress');
  const navigate = useNavigate();
  const studentId = student.id;
  const [params] = useSearchParams();
  const tab = params.get('tab') === 'tien-do' ? 'tien-do' : 'ho-so';
  // A real `<Link>` href, not an onClick — port guide + brief both require an
  // <a>/<Link> here (ctrl/cmd-click opens a new tab, middle-click too), which
  // a button-with-onClick can't do. Preserves every OTHER search param, not
  // just clears/sets `tab`.
  const tabHref = (next: 'ho-so' | 'tien-do') => {
    const p = new URLSearchParams(params);
    if (next === 'ho-so') p.delete('tab'); else p.set('tab', next);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  };
  const { data: enrollments } = useEnrollments(studentId);
  const { mutate: updateStudent } = useUpdateStudent(studentId);
  const [open, setOpen] = useState(false);
  const [assignEnrollmentId, setAssignEnrollmentId] = useState<string | null>(null);
  const canManageEnrollments = useCan('manage:enrollments');
  const canManageStudents = useCan('manage:students');
  const canManageClients = useCan('manage:clients');
  const { actions: menuActions, dialogs } = useStudentActions(student, navigate);

  const age = student.dateOfBirth ? ageOf(student.dateOfBirth) : null;
  const phone = student.client?.zaloPhone ?? null;
  const enrollmentCount = enrollments?.length ?? 0;

  // Tags are editable right here now (13 §2) — "Thêm tag" was never a header
  // action. Same optimistic-free pattern ClientHeader uses: one PUT per change.
  const addTag = (tag: string) => {
    if (student.tags.includes(tag)) return;
    updateStudent({ tags: [...student.tags, tag] });
  };
  const removeTag = (tag: string) => updateStudent({ tags: student.tags.filter((x) => x !== tag) });

  const actions: PageHeaderAction[] = [
    {
      key: 'add-enrollment',
      label: t('detail.addEnrollment'),
      icon: Plus,
      onClick: () => setOpen(true),
      disabledReason: canManageEnrollments ? undefined : tCommon('rbac.adminOnly'),
    },
    ...menuActions,
  ];

  return (
    <div className="mx-auto w-full max-w-4xl p-8">
      <PageHeader
        size="md"
        back={{ label: t('detail.back'), onClick: () => navigate(-1) }}
        title={
          <>
            {student.name}
            {student.nickname && (
              <span className="ml-2 text-2xl font-normal text-muted-foreground">({student.nickname})</span>
            )}
          </>
        }
        badge={
          <>
            <Badge variant="muted">{t('detail.identityBadge')}</Badge>
            {student.status === 'INACTIVE' && <Badge variant="muted">{t('status.INACTIVE')}</Badge>}
          </>
        }
        actions={actions}
      />

      {/* Identity block — no card, no avatar (13 §2). Line 1 is the facts you can
          act on (open the payer, tap the phone), line 2 is reference-only, line 3
          is the editable tags. */}
      <div className="mb-[25.6px]">
        {/* THI-129: for a MEMBER the API returns `client: null`, so this line
            would render "Người trả phí: —" — a labelled blank that reads as
            missing data rather than as a boundary. Drop the line entirely. */}
        {canManageClients && (
          <p className="text-sm text-surface-500">
            {t('detail.payer')}:{' '}
            {student.client ? (
              <Link to={`/clients/${student.client.id}`} className="font-medium text-brand-700 hover:underline">
                {student.client.name}
              </Link>
            ) : (
              '—'
            )}
            {phone && (
              <>
                {' · '}
                {/* tel: so a phone dials it, and the strip stays a link on desktop
                    rather than dead text a teacher has to retype. */}
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="tabular-nums hover:underline">
                  {phone}
                </a>
              </>
            )}
          </p>
        )}
        <p className="mt-[3.2px] text-[13px] text-surface-400">
          {student.gender && (
            <>{student.gender === 'MALE' ? t('fields.genderMale') : t('fields.genderFemale')}{' · '}</>
          )}
          {student.dateOfBirth && (
            <>
              {/* 390 keeps the age and drops the full date (13 §6). CSS, not
                  useIsMobile() — that hook is false on its first render. */}
              <span className="hidden sm:inline">
                {t('detail.dob')}: <span className="tabular-nums">{format(new Date(student.dateOfBirth), 'dd/MM/yyyy')}</span>
                {age !== null && ` (${t('detail.age', { count: age })})`}
              </span>
              {age !== null && <span className="sm:hidden">{t('detail.age', { count: age })}</span>}
              {' · '}
            </>
          )}
          {t('detail.added')}: <span className="tabular-nums">{format(new Date(student.createdAt), 'dd/MM/yyyy')}</span>
        </p>
        <div className="mt-2">
          <Tags
            tags={student.tags}
            mode={canManageStudents ? 'edit' : 'read'}
            onAdd={addTag}
            onRemove={removeTag}
          />
        </div>
        {student.notes && (
          // "1 dòng muted" in 13 §2 means one line ITEM in the block, not a
          // truncation: silently clipping a teacher's own note would hide
          // information behind nothing.
          <p className="mt-1.5 whitespace-pre-wrap text-[13px] text-surface-400">
            {t('detail.notes')}: {student.notes}
          </p>
        )}
      </div>

      {/* Two content GROUPS became three (đăng ký, hoạt động, tiến độ), which
          is what turns Luật 13 §1's "tab khi ≥ 3 nhóm" on. The tab lives in
          the URL so the sweep's "Hồ sơ em" link can land on the right one. */}
      <div className="mb-[19.2px] flex border-b border-surface-200">
        {(['ho-so', 'tien-do'] as const).map((key) => (
          <Link
            key={key}
            to={tabHref(key)}
            className={cn(
              '-mb-px h-[44px] flex-1 border-b-2 text-[13.5px] font-medium sm:flex-none sm:px-4',
              tab === key
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-surface-500 hover:text-surface-700',
            )}
          >
            {key === 'ho-so' ? tProgress('progress.profileTab') : tProgress('progress.tab')}
          </Link>
        ))}
      </div>

      {tab === 'ho-so' && (
        <>
          <section>
            {/* The heading keeps its own element; the link is its sibling, not a
                child of the <h2> — a heading whose accessible name includes a
                navigation label reads wrong in a landmark list. */}
            <div className="mb-[9.6px] flex flex-wrap items-center gap-2">
              <h2 className="font-display text-[15px] font-semibold tracking-[-0.012em] text-surface-800">
                {t('detail.enrollments')}{' '}
                {enrollmentCount > 0 && <span className="font-normal text-surface-400">({enrollmentCount})</span>}
              </h2>
              {/* No RBAC gate: `/attendance` is read-only, so a MEMBER gets it too.
                  44px tap area pinned in px (`--spacing` is 0.2rem); `sm:min-h-0`
                  keeps the desktop row its own height, matching the section heading. */}
              <Link
                to={`/attendance?student=${studentId}`}
                className="ml-auto inline-flex min-h-[44px] items-center text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline sm:min-h-0"
              >
                {t('detail.viewMonthAttendance')}
              </Link>
            </div>
            <div className="space-y-[9.6px]">
              {enrollmentCount === 0 ? (
                <div className="rounded-md border border-surface-200 bg-card">
                  <EmptyState
                    kind="teach"
                    icon={BookOpen}
                    title={t('detail.emptyEnrollmentsTitle')}
                    body={t('detail.emptyEnrollmentsBody')}
                    action={
                      canManageEnrollments ? { label: t('detail.addEnrollment'), onClick: () => setOpen(true) } : undefined
                    }
                  />
                </div>
              ) : (
                enrollments!.map((e) => (
                  <EnrollmentCard
                    key={e.id}
                    enrollment={e}
                    studentId={studentId}
                    onAssign={() => setAssignEnrollmentId(e.id)}
                  />
                ))
              )}
            </div>
          </section>

          {canManageStudents && <StudentActivitySection studentId={studentId} />}
        </>
      )}

      {tab === 'tien-do' && (
        <ProgressTab student={student} aside={<ProgressLinkBlock student={student} />} />
      )}

      {dialogs}

      <AddEnrollmentDialog
        open={open}
        onOpenChange={setOpen}
        studentId={studentId}
        onCreated={(newEnrollmentId) => setAssignEnrollmentId(newEnrollmentId)}
      />

      {enrollments?.map((e) => (
        <AssignDialog
          key={e.id}
          open={assignEnrollmentId === e.id}
          onOpenChange={(o) => setAssignEnrollmentId(o ? e.id : null)}
          enrollmentId={e.id}
          subjectId={e.subjectId}
          studentName={student.name}
        />
      ))}
    </div>
  );
}

function EditStudentDialog({
  student, open, onOpenChange,
}: {
  student: StudentWithClient;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader><DialogTitle>{t('detail.editDialog.title')}</DialogTitle></DialogHeader>
        {/* Mounted only while open so its fields always initialize fresh from the current student
            (no effect-driven resync needed). */}
        {open && <EditStudentForm student={student} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function EditStudentForm({
  student, onOpenChange,
}: {
  student: StudentWithClient;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  const { mutate: update, isPending } = useUpdateStudent(student.id);
  const { data: clients } = useClientOptions();
  const [values, setValues] = useState<StudentIdentityValues>({
    name: student.name,
    nickname: student.nickname ?? '',
    dateOfBirth: student.dateOfBirth ? student.dateOfBirth.slice(0, 10) : '',
    gender: student.gender,
  });
  const patch = (p: Partial<StudentIdentityValues>) => setValues((v) => ({ ...v, ...p }));
  const [notes, setNotes] = useState(student.notes ?? '');
  const [clientId, setClientId] = useState(student.clientId ?? '');
  const [localTags, setLocalTags] = useState<string[]>(student.tags);

  const addTag = (tag: string) => setLocalTags((prev) => [...prev, tag]);
  const removeTag = (tag: string) => setLocalTags((prev) => prev.filter((t2) => t2 !== tag));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const payerChanged = clientId !== student.clientId;
    update(
      {
        name: values.name,
        nickname: values.nickname || null,
        dateOfBirth: values.dateOfBirth || null,
        gender: values.gender,
        notes: notes || null,
        tags: localTags,
        ...(payerChanged ? { clientId } : {}),
      },
      {
        onSuccess: (res) => {
          onOpenChange(false);
          toast.success(t('detail.editDialog.success'));
          // I8: reassignment already succeeded server-side — this is just a heads-up
          // that pre-existing invoices stay with the previous payer.
          if (payerChanged && (res.pendingInvoiceCount ?? 0) > 0) {
            toast.info(t('detail.editDialog.payerReassignNotice', { count: res.pendingInvoiceCount }));
          }
        },
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <div className="grid gap-4 py-2">
        <StudentIdentityFields values={values} onChange={patch} idPrefix="edit" markRequired />
        <div className="grid gap-2">
          <Label htmlFor="epayer">{t('detail.editDialog.payer')}</Label>
          <ClientCombobox
            options={clients ?? []}
            value={clientId}
            onSelect={setClientId}
            placeholder="—"
            searchPlaceholder={t('create.payerSearchPlaceholder')}
            emptyText={t('create.payerEmpty')}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="enotes">{t('detail.editDialog.notes')}</Label>
          <Textarea id="enotes" value={notes} onChange={(ev) => setNotes(ev.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label>{t('detail.editDialog.tags')}</Label>
          <Tags tags={localTags} mode="edit" onAdd={addTag} onRemove={removeTag} />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('detail.editDialog.cancel')}
        </Button>
        <Button type="submit" disabled={isPending || !values.name}>
          {isPending ? t('detail.editDialog.submitting') : t('detail.editDialog.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function AddEnrollmentDialog({
  open, onOpenChange, studentId, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  studentId: string;
  /** The new enrollment's id, once the create call resolves — StudentDetailView
   *  uses this to open AssignDialog for it immediately (spec §5 "Student page":
   *  "After AddEnrollmentForm succeeds, open AssignDialog for the new
   *  enrollment"), so a teacher lands straight in Xếp lớp without a second click. */
  onCreated: (enrollmentId: string) => void;
}) {
  const { t } = useTranslation('students');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader><DialogTitle>{t('enrollment.title')}</DialogTitle></DialogHeader>
        {/* Mounted only while open so chargeAbsent's initial value always reads the org's
            current default fresh (useOrgSettings may still be loading when this dialog's
            parent first mounts, well before the dialog is ever opened). */}
        {open && <AddEnrollmentForm onOpenChange={onOpenChange} studentId={studentId} onCreated={onCreated} />}
      </DialogContent>
    </Dialog>
  );
}

function AddEnrollmentForm({
  onOpenChange, studentId, onCreated,
}: {
  onOpenChange: (o: boolean) => void;
  studentId: string;
  onCreated: (enrollmentId: string) => void;
}) {
  const { t } = useTranslation('students');
  const { data: subjects } = useSubjects();
  const { data: orgSettings } = useOrgSettings();
  const { mutate: create, isPending } = useCreateEnrollment();

  const [subjectId, setSubjectId] = useState('');
  // "Hình thức học" collapses pricingType × billingPeriod into one choice
  // (Addendum 2026-08-24 §2) — the pair confused first-time users. Derived
  // back into the two BE fields at submit; see `pricingTypeOf`/`submit`.
  const [mode, setMode] = useState<EnrollmentMode>('SESSION_MONTHLY');
  const [amount, setAmount] = useState('');
  const [termSize, setTermSize] = useState('');
  // Defaults from the org's policy (Enrollment.chargeAbsent), not hardcoded — it
  // remains editable per enrollment below, this only sets the starting value.
  // useOrgSettings() is frequently still loading at this component's first
  // render (this form only mounts once the dialog opens, but that can still
  // race the org-settings fetch), so the value is derived during render
  // instead of captured once via useState's initializer: `null` means "the
  // teacher hasn't touched this control yet", which keeps it live-following
  // the org default as that query resolves, without an effect.
  const [chargeAbsentOverride, setChargeAbsentOverride] = useState<boolean | null>(null);
  const chargeAbsent = chargeAbsentOverride ?? orgSettings?.defaultChargeAbsent ?? false;

  const isPerSession = mode !== 'MONTHLY_FLAT';
  const pricingTypeOf = (m: EnrollmentMode): PricingType => (m === 'MONTHLY_FLAT' ? 'MONTHLY_FLAT' : 'PER_SESSION');

  const reset = () => {
    setSubjectId('');
    setMode('SESSION_MONTHLY');
    setAmount('');
    setTermSize('');
    setChargeAbsentOverride(null);
  };

  const prefillAmount = (subjectIdVal: string, pricing: PricingType) => {
    const s = subjects?.find((x) => x.id === subjectIdVal);
    const prefill = pricing === 'PER_SESSION' ? s?.defaultRate : s?.defaultMonthlyRate;
    if (prefill != null) setAmount(String(prefill));
  };

  const onPickSubject = (sid: string) => {
    setSubjectId(sid);
    prefillAmount(sid, pricingTypeOf(mode));
  };

  const onPickMode = (m: EnrollmentMode) => {
    setMode(m);
    prefillAmount(subjectId, pricingTypeOf(m));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    let payload: CreateEnrollmentInput;
    if (mode === 'MONTHLY_FLAT') {
      payload = { studentId, subjectId, pricingType: 'MONTHLY_FLAT', monthlyRate: amt };
    } else {
      payload = {
        studentId,
        subjectId,
        pricingType: 'PER_SESSION',
        ratePerSession: amt,
        chargeAbsent,
        billingPeriod: mode === 'SESSION_TERM' ? 'TERM' : 'MONTHLY',
        ...(mode === 'SESSION_TERM' ? { termSize: Number(termSize) } : {}),
      };
    }
    create(payload, {
      onSuccess: (created) => {
        onOpenChange(false);
        reset();
        onCreated(created.id);
      },
    });
  };

  const amountLabel = isPerSession ? t('enrollment.ratePerSession') : t('enrollment.monthlyRate');

  const canSubmit = subjectId && amount && (mode !== 'SESSION_TERM' || termSize);

  return (
    <form onSubmit={submit}>
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label>{t('enrollment.subject')}</Label>
          <Select value={subjectId} onValueChange={onPickSubject}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {(subjects ?? []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-2">
          <Label>{t('enrollment.mode.label')}</Label>
          <div className="grid gap-2">
            {ENROLLMENT_MODES.map((m) => {
              const isSelected = mode === m;
              const isRecommended = m === 'SESSION_MONTHLY';
              return (
                <button
                  key={m}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onPickMode(m)}
                  // 56px pinned in px: --spacing is 0.2rem app-wide, so p-3
                  // alone lands well under the 44px minimum touch target.
                  className={cn(
                    'flex min-h-[56px] items-center gap-3 p-3 border text-left transition-all',
                    'hover:border-brand-300 hover:bg-brand-50/50',
                    isSelected
                      ? 'border-brand-500 bg-brand-50/70 ring-1 ring-brand-500'
                      : 'border-surface-200 bg-surface-0',
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn('text-sm font-semibold', isSelected ? 'text-brand-700' : 'text-surface-800')}>
                        {t(`enrollment.mode.${m}.label`)}
                      </p>
                      {isRecommended && <Badge variant="success">{t('enrollment.mode.recommended')}</Badge>}
                    </div>
                    <p className="mt-0.5 text-xs text-surface-500">{t(`enrollment.mode.${m}.desc`)}</p>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 bg-brand-500 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="amt">{amountLabel}</Label>
          <Input id="amt" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </div>
        {mode === 'SESSION_TERM' && (
          <div className="grid gap-2">
            <Label htmlFor="tsize">{t('enrollment.termSize')}</Label>
            <Input id="tsize" type="number" min="1" value={termSize} onChange={(e) => setTermSize(e.target.value)} required />
          </div>
        )}
        {isPerSession && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={chargeAbsent} onChange={(e) => setChargeAbsentOverride(e.target.checked)} />
            {t('enrollment.chargeAbsent')}
          </label>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('enrollment.cancel')}</Button>
        <Button type="submit" disabled={isPending || !canSubmit}>
          {isPending ? t('enrollment.submitting') : t('enrollment.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
